import React, { useEffect, useRef, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import '../../App.css';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faCartShopping,
  faDeleteLeft,
  faFilePdf,
  faFolderMinus,
  faFolderPlus,
  faPenToSquare,
  faShareAlt,
  faTrashCan,
} from '@fortawesome/free-solid-svg-icons';
import { generatePDF, getURLSearchParams, goToTopOfPage } from '../../helper/helper';
import { archiveBook, handleDeleteBook, unArchiveBook } from '../Builder/services/builderHelpers';
import Footer from '../../components/Common/Footer';
import NavigationBar from '../../components/NavigationBar/NavigationBar';
import Loader from '../../components/Common/Loader';
import Dialogs from '../../components/Common/Dialog';
import { BookModel } from '../../interfaces/book.interface';
import { AppDispatch, RootState } from '../../store/store';
import { useDispatch, useSelector } from 'react-redux';
import { runEditStory } from '../../store/storySlice';
import LoadingSpinner from '../../components/LoadingSpinner/LoadingSpinner';
import { fetchBookDataService, sendBookView, sendEmailBookAccess, sortedPages } from './services/BookFlip.service';
import BookPreview from './components/BookFlipPreview';
import { Toast } from '../../components/Common/Toast';
import { UserLocalStorageKeysEnum, UserRoleEnum } from '../../interfaces/user.interface';
import { CONFIG } from '../../config/mainConfig';
import { setShowShareModalData } from '../../store/appSlice';
import Modal from '../../components/Common/modal/Modal';
import FlipBookAccessEmail from './components/BookFlipAccessEmail';
import { useStudentVPCStatus } from '../../hooks/useStudentVPCStatus';
import OnTourBar from '../../components/Common/OnTourBar';
import { TeacherOnboardingModel } from '../../interfaces/teacher.interface';
import { TeacherOnboardingStatusEnum } from '../../interfaces/user.interface';
import { getOnboardingResumeRoute, updateTeacherOnboardingStatus } from '../Teacher/utils/onboarding.utils';

interface FlipBookProps {
  bookIdAdmin?: string | null;
  isShare?: boolean;
}

interface PageFlipInstance {
  pageFlip: () => { flip: (page: number) => void };
}

function FlipBook({ bookIdAdmin = null, isShare = false }: FlipBookProps) {
  const navigateTo = useNavigate();
  const user = useSelector((state: RootState) => state.user.user);
  const teacherOnboarding = useSelector((state: RootState) => state.user.teacherOnboarding) as TeacherOnboardingModel | null;
  const isTeacherOnboarding = user?.teacherOnboardingStatus === TeacherOnboardingStatusEnum.BookOnboarding;
  const [statusLoading, setStatusLoading] = useState<boolean>(false);
  const queryParams = getURLSearchParams();
  const inlineQueryParams = getURLSearchParams(true);
  const { vpcRequired, vpcComplete, loading, teacherState, isVPCShareBlocked } = useStudentVPCStatus();

  const [totalPages, setTotalPages] = useState<number>(0);
  const [loader, setLoader] = useState<boolean>(false);
  const [loaderPage, setLoaderPage] = useState<boolean>(true);
  const [bookData, setBookData] = useState<BookModel | null>(null);
  const flipBookRef = useRef<PageFlipInstance | null>(null);
  const { bookId } = useParams(); // Extract bookId from URL
  const [archiveLoader, setArchivedLoader] = useState<boolean>(false);
  const [deleteLoader, setDeleteLoader] = useState<boolean>(false);
  const [selectedBook, setSelectedBook] = useState<Partial<BookModel>>({ bookId: undefined });
  const [openDialog, setOpenDialog] = useState<boolean>(false);
  const [stripeSubscriptionStatus, setStripeSubscriptionStatus] = useState<boolean>(true);
  const dispatch: AppDispatch = useDispatch();

  const fetchBookData = async () => {
    const { data, totalPages } = await fetchBookDataService(
      bookIdAdmin,
      bookId!,
      isShare,
      queryParams.get(CONFIG.HEADERS.BOOK_SHARE),
      queryParams.get(CONFIG.HEADERS.BOOK_SHARE_QR) === 'true',
      navigateTo,
    );
    setLoaderPage(false);
    setBookData({
      ...(data || {}),
      showRequireEmailPopup:
        sessionStorage.getItem('showRequireEmailPopup') === 'false' ? false : data?.showRequireEmailPopup,
    });
    setTotalPages(totalPages);

    if (sessionStorage.getItem('viewedBook') !== 'true') {
      sessionStorage.setItem('viewedBook', 'true');
      if (!data?.showRequireEmailPopup) {
        sendBookView(bookId!, user?.email || null, queryParams.get(CONFIG.HEADERS.BOOK_SHARE), undefined, (request) => {
          if (request.hasOwnProperty('showRequireEmailPopup')) {
            setBookData((prevState) => ({
              ...prevState,
              showRequireEmailPopup: request['showRequireEmailPopup'],
            }));
          }
        })
          .then()
          .catch(() => {});
      }
    }
  };

  useEffect(() => {
    fetchBookData();
  }, [bookId, bookIdAdmin]);


  useEffect(() => {
    user && setStripeSubscriptionStatus(!!user?.stripeSubscriptionId || !!user.isTrialFreeAccess);
  }, []);

  useEffect(() => {
    goToTopOfPage();
    const currentBookId = localStorage.getItem(UserLocalStorageKeysEnum.BOOK_ID);
    if (currentBookId !== bookId) {
      localStorage.setItem(UserLocalStorageKeysEnum.BOOK_ID, bookId!);
    }
  });

  const handleEditBook = (book: BookModel) => {
    stripeSubscriptionStatus
      ? dispatch(runEditStory({ book, navigate: navigateTo }))
      : Toast('You need an active membership to edit your story', true);
  };

  const createPDF = async () => {
    setLoader(true);
    await generatePDF(bookId!, isShare);
    await fetchBookData();
    setLoader(false);
  };

  const sendEmailAccess = async (email: string) => {
    await sendEmailBookAccess(bookId!, email, queryParams.get(CONFIG.HEADERS.BOOK_SHARE), () => {
      sessionStorage.setItem('showRequireEmailPopup', 'false');
      sessionStorage.setItem('userEmailAddress', email);
      setBookData((prevState) => ({
        ...prevState,
        showRequireEmailPopup: false,
      }));
    });
  };

  const scrollToFirstPage = () => {
    if (flipBookRef.current) {
      flipBookRef.current.pageFlip().flip(0);
    }
  };

  const scrollToLastPage = () => {
    if (flipBookRef.current) {
      flipBookRef.current.pageFlip().flip(totalPages + 5);
    }
  };

  const isPreviewMode = () => {
    return inlineQueryParams.get('mode') === 'preview';
  };

  const getLibraryPath = () => {
    return user ? (user?.isTeacherMode ? '/classroom/library' : '/library') : '';
  };

  const handlePurchase = () => {
    if (isShare) {
      navigateTo(`/book/purchase/${bookId}`);
    } else {
      navigateTo(`/bookpurchase/${bookId}`);
    }
  };

  if (!bookData) {
    return (
      <div>
        {!bookIdAdmin && (isTeacherOnboarding ? (
          <OnTourBar stepNumber={5} totalSteps={5} title="Your book is ready!" description="Review your flipbook, then complete your teacher setup" showButton={false} />
        ) : (
          <NavigationBar />
        ))}
        <LoadingSpinner size="w-16 h-16" height="h-100" />
      </div>
    );
  }

  // Show VPC message if required for students viewing shared books
  if (isShare && isVPCShareBlocked()) {
    return (
      <div className="min-h-screen bg-gray-50 flex flex-col justify-center items-center p-4">
        <div className="max-w-md text-center">
          <div className="mb-6">
            <svg className="mx-auto h-16 w-16 text-yellow-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z"
              />
            </svg>
          </div>

          <h1 className="text-2xl font-bold text-gray-900 mb-4">Book Viewing Not Available</h1>

          <p className="text-gray-600 mb-4">Requires Verified Parental Consent to view shared books.</p>

          <p className="text-sm text-gray-500 mb-6">
            Please ask your teacher to complete the parental consent form before you can view this book.
          </p>

          <button
            onClick={() => window.history.back()}
            className="bg-storyblue text-white px-6 py-2 rounded-lg hover:bg-storyblueSecondLightText transition-colors"
          >
            Go Back
          </button>
        </div>
      </div>
    );
  }

  return (
    <div>
      {!bookIdAdmin && (isTeacherOnboarding ? (
        <OnTourBar
          stepNumber={5}
          totalSteps={5}
          title="Your book is ready!"
          description="Review your flipbook, then complete your teacher setup"
          buttonText="Next: Complete Setup"
          onButtonClick={async () => {
            setStatusLoading(true);
            try {
              const success = await updateTeacherOnboardingStatus(TeacherOnboardingStatusEnum.TeacherInfo);
              if (!success) {
                console.error('Failed to update onboarding status to TeacherInfo');
                return;
              }
              navigateTo(getOnboardingResumeRoute(teacherOnboarding));
            } finally {
              setStatusLoading(false);
            }
          }}
          showButton={true}
          showBackButton={true}
          onBackClick={() => navigateTo('/')}
          loading={statusLoading}
          disabled={statusLoading}
        />
      ) : (
        <NavigationBar />
      ))}
      <div className="bg-storytan p-[.25rem] md:p-4 mx-auto">
        {loaderPage && isShare && (
          <div className={`App bg-white min-h-screen fixed w-full z-50 left-0 top-0`}>
            <div className="h-[100vh] flex justify-center items-center">
              <img className="w-16 h-16" src="/loading.gif" alt="Loading" />
            </div>
          </div>
        )}
        <div>
          {!bookIdAdmin && !isTeacherOnboarding && (
            <div className="flex justify-center items-center px-2 gap-[.7rem] md:gap-4 mt-4 rounded bg-white h-[5rem] w-full">
              {!isShare && (
                <Link
                  to={isPreviewMode() ? '' : getLibraryPath()}
                  onClick={() => isPreviewMode() && bookData && handleEditBook(bookData)}
                  rel="noopener noreferrer"
                >
                  <div className="cursor-pointer">
                    <FontAwesomeIcon icon={faDeleteLeft} className="text-2xl md:text-3xl text-storyblue" title="Back" />
                    <p className="text-storyblue text-[13px] leading-tight md:text-base">Back</p>
                  </div>
                </Link>
              )}

              {bookData && !isShare && (
                <Link to="#" rel="noopener noreferrer" onClick={() => handleEditBook(bookData)}>
                  <div className="cursor-pointer">
                    <FontAwesomeIcon
                      icon={faPenToSquare}
                      className={`text-2xl md:text-3xl ${stripeSubscriptionStatus ? 'text-storyblue' : 'inactive-menu-item'}`}
                      title="Purchase"
                    />
                    <p
                      className={`${stripeSubscriptionStatus ? 'text-storyblue' : 'inactive-menu-item'} text-[13px] leading-tight md:text-base`}
                    >
                      Edit story
                    </p>
                  </div>
                </Link>
              )}
              {bookData && !bookData?.pdfUrl ? (
                <Link
                  to="#"
                  onClick={() => {
                    if (!loader) {
                      createPDF();
                    }
                  }}
                  rel="noopener noreferrer"
                  className={`cursor-pointer ${loader ? 'disabled-link' : ''}`}
                  style={{ pointerEvents: loader ? 'none' : 'auto' }}
                >
                  <div className="flex flex-col items-center justify-center">
                    {!loader ? (
                      <FontAwesomeIcon
                        icon={faFilePdf}
                        className="text-2xl md:text-3xl text-storyblue"
                        title="Generate PDF"
                      />
                    ) : (
                      <Loader loader={loader} />
                    )}
                    <p className="text-storyblue text-[13px] leading-tight md:text-base">Generate PDF</p>
                  </div>
                </Link>
              ) : (
                <Link to={bookData?.pdfUrl!} target="_blank" rel="noopener noreferrer">
                  <div className="cursor-pointer">
                    <FontAwesomeIcon
                      icon={faFilePdf}
                      className=" text-2xl md:text-3xl text-storyblue"
                      title="View PDF"
                    />
                    <p className="text-storyblue text-[13px]  leading-tight md:text-base">View PDF</p>
                  </div>
                </Link>
              )}
              {!isShare && user?.role !== UserRoleEnum.student && (
                <Link onClick={() => dispatch(setShowShareModalData(bookData))} to="#" rel="noopener noreferrer">
                  <div className="cursor-pointer">
                    <FontAwesomeIcon icon={faShareAlt} className="text-2xl md:text-3xl text-storyblue" title="Share" />
                    <p className="text-storyblue text-[13px] md:text-base">Share</p>
                  </div>
                </Link>
              )}
              {!isShare && user?.role === UserRoleEnum.student && (
                <>
                  {loading ? (
                    <div className="cursor-pointer p-4">
                      <Loader loader={loading} dimentions={'w-7 h-7'} />
                    </div>
                  ) : (
                    <Link
                      className={`${isVPCShareBlocked() ? 'text-gray-400 opacity-50 cursor-not-allowed' : ''}`}
                      onClick={(e) =>
                        isVPCShareBlocked() ? e.preventDefault() : dispatch(setShowShareModalData(bookData))
                      }
                      to="#"
                      rel="noopener noreferrer"
                      title={isVPCShareBlocked() ? 'Share not available due to VPC requirements' : 'Share book'}
                    >
                      <div className="cursor-pointer">
                        <FontAwesomeIcon icon={faShareAlt} className="text-2xl md:text-3xl text-storyblue" />
                        <p className="text-storyblue text-[13px] md:text-base">Share</p>
                      </div>
                    </Link>
                  )}
                </>
              )}
              {user?.role !== UserRoleEnum.student && (
                <>
                  {bookData && !isShare && !bookData?.archived && (
                    <Link
                      onClick={() => {
                        archiveBook(bookData?.bookId!, fetchBookData, setArchivedLoader);
                      }}
                      to="#"
                      rel="noopener noreferrer"
                      className={`cursor-pointer ${archiveLoader ? 'disabled-link' : ''}`}
                      style={{ pointerEvents: archiveLoader ? 'none' : 'auto' }}
                    >
                      <div className="flex flex-col items-center justify-center">
                        {!archiveLoader ? (
                          <FontAwesomeIcon
                            icon={faFolderPlus}
                            className="text-2xl md:text-3xl text-storyblue"
                            title="Archive"
                          />
                        ) : (
                          <Loader loader={archiveLoader} />
                        )}
                        <p className="text-storyblue text-[13px]  leading-tight md:text-base">Archive</p>
                      </div>
                    </Link>
                  )}
                  {bookData && !isShare && bookData?.archived && (
                    <Link
                      onClick={() => {
                        unArchiveBook(bookData?.bookId!, fetchBookData, setArchivedLoader);
                      }}
                      to="#"
                      rel="noopener noreferrer"
                    >
                      <div className="cursor-pointer">
                        {!archiveLoader ? (
                          <FontAwesomeIcon
                            icon={faFolderMinus}
                            className="text-2xl md:text-3xl text-storyblue"
                            title="Archive"
                          />
                        ) : (
                          <Loader loader={archiveLoader} />
                        )}
                        <p className="text-storyblue text-[13px]  leading-tight md:text-base">UnArchive</p>
                      </div>
                    </Link>
                  )}
                </>
              )}
              {user?.role !== UserRoleEnum.student && (
                <Link onClick={handlePurchase} to="#" rel="noopener noreferrer">
                  <div className="cursor-pointer">
                    <FontAwesomeIcon
                      icon={faCartShopping}
                      className="text-2xl md:text-3xl text-storyblue"
                      title="Purchase"
                    />
                    <p className="text-storyblue text-[13px] md:text-base">Purchase</p>
                  </div>
                </Link>
              )}
              {/*{user?.role === UserRoleEnum.student && (*/}
              {/*  <>*/}
              {/*    {loading ? (*/}
              {/*      <div className="cursor-pointer p-4">*/}
              {/*        <Loader loader={loading} dimentions={'w-7 h-7'} />*/}
              {/*      </div>*/}
              {/*    ) : (*/}
              {/*      <Link*/}
              {/*        className={`${isVPCBlocked() ? 'text-gray-400 opacity-50 cursor-not-allowed' : ''}`}*/}
              {/*        onClick={(e) => (isVPCBlocked() ? e.preventDefault() : handlePurchase())}*/}
              {/*        to="#"*/}
              {/*        rel="noopener noreferrer"*/}
              {/*      >*/}
              {/*        <div className="cursor-pointer">*/}
              {/*          <FontAwesomeIcon icon={faCartShopping} className="text-2xl md:text-3xl text-storyblue" />*/}
              {/*          <p className="text-storyblue text-[13px] md:text-base">Purchase</p>*/}
              {/*        </div>*/}
              {/*      </Link>*/}
              {/*    )}*/}
              {/*  </>*/}
              {/*)}*/}
              {!isShare && user?.role !== UserRoleEnum.student && (
                <Link
                  onClick={() => {
                    setSelectedBook({
                      ...selectedBook,
                      bookId: bookData?.bookId,
                    });
                    setOpenDialog(true);
                  }}
                  to="#"
                  rel="noopener noreferrer"
                >
                  <div className="cursor-pointer">
                    <FontAwesomeIcon icon={faTrashCan} className="text-2xl md:text-3xl text-storyblue" title="Delete" />

                    <p className="text-storyblue text-[13px] md:text-base">Delete</p>
                  </div>
                </Link>
              )}
            </div>
          )}

          <div className={`max-w-7xl mx-auto md:px-6 lg:px-8 ${bookIdAdmin ? 'h-[100%]' : ' min-h-screen'}  mt-8`}>
            <BookPreview
              bookData={bookData}
              isShare={isShare}
              totalPages={totalPages}
              bookIdAdmin={bookIdAdmin}
              flipBookRef={flipBookRef}
              sortedPages={() => sortedPages(bookData?.pages!)}
              user={user}
            />
            {/*<div*/}
            {/*  className={` ${bookIdAdmin ? 'w-full  lg:w-[92%]' : 'w-full'*/}
            {/*  } flex flex-col items-end gap-2 mt-4 mr-4`}*/}
            {/*>*/}
            {/*  <strong className=" text-lg text-storyblue  w-full md:w-auto">*Click on corner of book to flip*/}
            {/*    pages</strong>*/}
            {/*  <div className="flex gap-4 w-full md:w-auto justify-center sm:justify-end sm:m-0">*/}
            {/*    <button*/}
            {/*      onClick={scrollToFirstPage}*/}
            {/*      className="text-sm font-bold text-white rounded-2xl px-4 py-2 bg-storyblueSecondLightText"*/}
            {/*    >*/}
            {/*      Go to First Page*/}
            {/*    </button>*/}
            {/*    <button*/}
            {/*      onClick={scrollToLastPage}*/}
            {/*      className="text-sm font-bold text-white rounded-2xl px-4 py-2 bg-storyblueSecondLightText"*/}
            {/*    >*/}
            {/*      Go to Last Page*/}
            {/*    </button>*/}
            {/*  </div>*/}
            {/*</div>*/}
          </div>
        </div>
      </div>
      {bookData?.showRequireEmailPopup && (
        <Modal
          title={'View Flip Book'}
          className={'global-absolute-popup-style monrope-font content-left'}
          showSubmitButton={false}
          showCloseButton={false}
          showTopRightCloseButton={false}
        >
          <FlipBookAccessEmail submit={sendEmailAccess}></FlipBookAccessEmail>
        </Modal>
      )}
      <Dialogs
        openDialog={openDialog}
        handelClose={() => setOpenDialog(false)}
        handelProcess={() => handleDeleteBook(selectedBook?.bookId!, setDeleteLoader, navigateTo)}
        confirmMessage="Are you sure you want to delete this Story?"
        loader={deleteLoader}
      />
      <Footer className="mb-0 pb-4" />
    </div>
  );
}

export default FlipBook;
